/*eslint-disable no-eval*/
import { AutocompleteChangeDetails, AutocompleteChangeReason, Box, Paper, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useTagValues } from "../../queries/tag-values/UseTagValues";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useForm  } from "react-hook-form";
import ManualEntryFormUtils, { ManualEntryFormDirtyType, ManualEntryFormMode, ManualEntryFormRefType, ManualEntryFormStateType, ManualEntryFormType, TagFormType, defaultManualEntryForm } from "./ManualEntryFormUtils";
import { Ref, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import Form from "../../components/forms/form/Form";
import { subscribe, unsubscribe } from "pubsub-js";
import { OFFLINE_PENDING_CHANGES_SAVED_TOPIC } from "../../common/models/pub-sub/PubSubTopics";
import LoadingZone from "../loading-zone/LoadingZone";
import OfflineDataService from "../../services/offline-data-service/OfflineDataService";
import { IeLynxObject } from "../../common/models/eLynxObject";
import Enumerable from "linq";
import { IEntityValueDeviceModel, IEntityValueTagModel } from "../../common/models/IEntityValueModels";
import _ from "lodash";
import { ALL_TAGS_CATEGORY_ID, ITagCategory, SUMMARY_LISTING_TAGS_CATEGORY_ID } from "../../common/models/ITagCategory";
import { useTranslation } from "react-i18next";
import ObjectTagValueUtils, { allTagsCategory } from "../object-tag-values/ObjectTagValuesUtils";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import NoData from "../no-data/NoData";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import ManualEntryFormSidesheet from "./ManualEntryFormSidesheet";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { DetailRow } from "./DetailRow";
import { HeaderCell } from "./HeaderCell";


export type DesktopManualEntryFormProps = {
    mode: ManualEntryFormMode
    date: Date | undefined,
    formState: ManualEntryFormStateType,
    onFormStateChanged: (newFormState: ManualEntryFormStateType) => void;
    elynxObject?: IeLynxObject;
    writedownMode?: boolean;
    configurationID?: number; // Optional, used for write down configuration
    filteredTags?: IEntityValueTagModel[],
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};


function DesktopManualEntryForm({
    mode,
    date,
    formState,
    onFormStateChanged,
    elynxObject,
    writedownMode,
    configurationID,
    filteredTags,
    setViewOptionState

}: DesktopManualEntryFormProps, ref: Ref<unknown> | undefined) {

    const [filterText, setFilterText] = useState('');
    const [categories, setCategories] = useState<ITagCategory[]>([]);
    const [viewAllTags, setViewAllTags] = useState<boolean>(false);
    const [viewAllWriteTags, setViewAllWriteTags] = useState<boolean>(false);
    const [offlineState] = useOfflineDetectContext();    
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const { t } = useTranslation();

    const offlineConfig = OfflineDataService.getOfflineConfig();

    const { data: dataResponse, refetch, status } = useQueryNotifications(useTagValues)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        objectID: elynxObject?.id ?? routeContext.contextObject!.id,
        objectType: elynxObject?.objectType ?? routeContext.contextObject!.objectType,
        configurationID: configurationID,
        date: date,
        offline: offlineState.offline,
        manualEntry: true,
        useLKVOffline: offlineConfig.useLKV
    });

    const { data: allDataResponse } = useQueryNotifications(useTagValues)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        objectID: elynxObject?.id ?? routeContext.contextObject!.id,
        objectType: elynxObject?.objectType ?? routeContext.contextObject!.objectType,
        configurationID: -1,
        date: date,
        offline: offlineState.offline,
        manualEntry: true,
        useLKVOffline: offlineConfig.useLKV
    });

    const form = useForm<ManualEntryFormType>({
        mode: "onChange",
         defaultValues: {...defaultManualEntryForm
        }
    });

    const {
        //handleSubmit,
        formState: {
            errors,
            isDirty,
            isValid,
            dirtyFields
        },
        //clearErrors,
        getValues,
        //setValue,
        //watch,
        //control,
        reset,
        //trigger,
        //setError
    } = form;

    const tolerance = (dataResponse as IEntityValueDeviceModel)?.gasAnalysisTolerance ?? 0;
    const tags = getValues('tags');

    const groupedTags = useMemo(() => {


        let allWriteTags: TagFormType[] = [];

        const model = ManualEntryFormUtils.mapToForm(mode, dataResponse, false, viewAllTags);

        if (viewAllWriteTags || configurationID === -1 || configurationID === undefined) {
        
            const feature = mode === ManualEntryFormMode.ManualEntry ? ObjectFeaturesEnum.ManualEntry : ObjectFeaturesEnum.Writedown;

            allWriteTags = tags.filter(v => (v.features & feature) > 0);
        }

        let filteredTags = Enumerable.from(model.tags)
            .join(dataResponse?.tags ?? [],
                o => o.id,
                v => v.id,
                (o, v) => o)
        .toArray();

        if (!viewAllTags) {

            const feature = mode === ManualEntryFormMode.ManualEntry ? ObjectFeaturesEnum.ManualEntry : ObjectFeaturesEnum.Writedown;

            filteredTags = filteredTags.filter(v => (v.features & feature) > 0);
        }

        filteredTags = Enumerable.from(filteredTags.concat(allWriteTags))
                            .distinct(o => o.id)
                            .toArray();

        filteredTags = Enumerable.from(tags).join(filteredTags,
            o => o.id,
            v => v.id,
            (o, v) => o)
            .toArray();


        const data = ManualEntryFormUtils.groupByCategory(filteredTags, categories, filterText);

        return data;

    },[
        mode,
        tags,
        configurationID,
        dataResponse,
        viewAllTags,
        viewAllWriteTags,
        categories,
        filterText
    ]);

    function getModifiedValues() {

        return ManualEntryFormUtils.getModifiedValues(date, dirtyFields as ManualEntryFormDirtyType, tags);
    }

    useImperativeHandle<unknown, ManualEntryFormRefType>(
        ref,
        () => ({
            // hasPendingChanges: () => { return isDirty; },
            // isValid: () => { return isValid },
            getChanges: getModifiedValues,
            getTags: () => {
                return dataResponse?.tags ?? [];
            }
        })
    );

    function selectCategory(
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<ITagCategory> | undefined
    ) {

        const newCategories = [...categories];

        let item = categories.find(o => o.id === details?.option.id);

        if (item) {

            switch (reason) {
                case 'selectOption':
                    item.selected = true;

                    if (item.id === ALL_TAGS_CATEGORY_ID || item.id === SUMMARY_LISTING_TAGS_CATEGORY_ID) {
                        newCategories.forEach(o => {
                            if (o.id !== item?.id) {
                                o.selected = false;
                            }
                        });
                    } else {

                        newCategories.filter(o => o.id === ALL_TAGS_CATEGORY_ID || o.id === SUMMARY_LISTING_TAGS_CATEGORY_ID)
                            .forEach(o => o.selected = false);
                    }

                    break;

                case 'removeOption':
                    item.selected = false;
                    break;

                default:
                    break;
            }
        }

        if (!newCategories.find(o => o.selected)) {
            // if no categories are selected, select the all tags category
            const allTagsItem = newCategories.find(o => o.id === ALL_TAGS_CATEGORY_ID);
            if (allTagsItem) {
                allTagsItem.selected = true;
            }
        }

        setCategories(newCategories);
    }

    function resetOptions() {
        const newCategories = [...categories];
        newCategories.forEach(o => o.selected = false);
        const allTagsItem = newCategories.find(o => o.id === ALL_TAGS_CATEGORY_ID);
        if (allTagsItem) {
            allTagsItem.selected = true;
        }   
        setCategories(newCategories);
        setFilterText('');
    }

    useEffect(() => {

        if (allDataResponse) {
            const model = _.cloneDeep(allDataResponse);
            if (filteredTags && filteredTags.length > 0) {

                model.tags = Enumerable.from(model.tags)
                                .join(filteredTags,
                                    o => o.id,
                                    v => v.id,
                                    (o, v) => o)
                                    .toArray();
            }

            reset(ManualEntryFormUtils.mapToForm(mode, model, offlineState.offline, true));
        }

    }, [
        mode,
        allDataResponse,
        filteredTags,
        offlineState.offline,
        reset
    ]);


    useEffect(() => {
        if (writedownMode && dataResponse) {

            // if categories are provided, use them instead of the ones from dataResponse
            const categoryList = ObjectTagValueUtils.getTagCategories(dataResponse, false);

            const model = _.cloneDeep(dataResponse);
            if (filteredTags && filteredTags.length > 0) {

                model.tags = Enumerable.from(model.tags)
                                .join(filteredTags,
                                    o => o.id,
                                    v => v.id,
                                    (o, v) => o)
                                    .toArray();

            }


            const tags = model.tags.filter(o => (((o.features ?? ObjectFeaturesEnum.None) & ObjectFeaturesEnum.Writedown) > 0) || viewAllTags);
            let categoriesInUse: ITagCategory[] = [allTagsCategory];
            
            categoriesInUse = categoriesInUse.concat(Enumerable.from(categoryList)
                .join(tags,
                    o => o.id,
                    v => v.categoryID,
                    (o, v) => o
                ).distinct(o => o.id)
                .toArray());
            
            setCategories(categoriesInUse);
        }
    }, [
        dataResponse,
        writedownMode,
        filteredTags,
        setCategories,
        viewAllTags
    ]);    

    useEffect(() => {

        const valid = mode === ManualEntryFormMode.WriteDown ? Object.keys(errors).length === 0 : isValid;        

        if (formState.isDirty !== isDirty ||
            formState.isValid !== valid) {
            onFormStateChanged({
                isDirty,
                isValid: valid
            });
        }
    }, [
        formState,
        isDirty,
        isValid,
        errors,
        mode,
        onFormStateChanged
    ]);

    useEffect(() => {
        
        if (setViewOptionState) {
            setViewOptionState(categories.filter(o => o.selected)
                            .some(o => o.id !== ALL_TAGS_CATEGORY_ID) || filterText !== '' ?
                                ViewOptionStateEnum.Changed : 
                                ViewOptionStateEnum.Default); 
        }

    }, [
        setViewOptionState,
        categories,
        filterText
    ]);

    //console.log(`isDirty: ${isDirty} dirtyfields: ${JSON.stringify(dirtyFields)} and ${JSON.stringify(errors)}`)
    //const hasErrors = mode === ManualEntryFormMode.WriteDown ? Object.keys(dirtyFields).length && Object.keys(errors).length ? true : false : 
    

    useEffect(() => {
        const token = subscribe(OFFLINE_PENDING_CHANGES_SAVED_TOPIC, () => refetch());

        return () => {
            if (token) {
                unsubscribe(token);
            }
        }
    }, [
        refetch
    ]);

    return (

        <ContentContainer>

            <ContentSection>

                <LoadingZone isLoading={status === 'loading'}>
                        
                    <Form form={form} sx={{ padding: 1 }}>
                        {
                            groupedTags.length === 0 ?
                                            
                                <Box sx={{
                                    padding: 1
                                }}>
                                    <NoData text={t('livedata.noTags')} />
                                </Box>
                            :
                            <TableContainer component={Paper} >
                                <Table 
                                    aria-label="write down tag table" 
                                    stickyHeader={true} 
                                    size="small"
                                >
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                '& > th': {
                                                    minWidth: '100px',
                                                }
                                            }}
                                        >
                                            <HeaderCell 
                                                text={getObjectTypeName(eLynxObjectTypeEnum.Tag, false)}
                                                sx={{
                                                    width: '400px',
                                                }}
                                            />
                                            <HeaderCell 
                                                sx={{
                                                    width: '300px'
                                                }}
                                                text={t('properties.currentValue')} 
                                            />
                                            <HeaderCell 
                                                text={t('properties.uomAbbreviation')} 
                                                sx={{
                                                    width: '75px'
                                                }}
                                            />
                                            <HeaderCell 
                                                text={t('properties.dateTime')} 
                                                sx={{
                                                    width: '200px'
                                                }}
                                            />
                                            <HeaderCell 
                                                sx={{
                                                    width: '300px'
                                                }}
                                                text={t('properties.newValue')} 
                                            />

                                        </TableRow>
                                    </TableHead>
                                    <TableBody>

                                        {groupedTags.map((o, index) => 
                                            <DetailRow key={index} 
                                                mode={mode} 
                                                item={o} 
                                                tolerance={tolerance} 
                                                viewAllTags={viewAllTags}

                                            />)}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                        }

                    </Form>

                </LoadingZone>
            </ContentSection>

            {
            writedownMode && 

            <ManualEntryFormSidesheet
                resetOptions={resetOptions}
                configurationEnabled={true}
                categories={categories}
                selectCategory={selectCategory}
                filterText={filterText}
                setFilterText={setFilterText}
                showAllTagsOption={true}
                viewAllTags={viewAllTags}
                setViewAllTags={setViewAllTags}
                viewAllWriteTags={viewAllWriteTags}
                setViewAllWriteTags={setViewAllWriteTags}
                showAllWriteTagsOption={configurationID !== -1}
            />
        }

    </ContentContainer>        
    );
}

export default forwardRef(DesktopManualEntryForm);